/**
 * OP-2 & OP-3: Work Orders List + Create
 * Respects feature entitlements for conditional field display
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { opsApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import { 
  Plus, Search, Package, RefreshCw, Loader2, ChevronRight, 
  MapPin, Thermometer, Clock
} from 'lucide-react';

export default function WorkOrdersPage() {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [features, setFeatures] = useState({ temp: { enabled: true }, weight: { enabled: false } });
  const [newWO, setNewWO] = useState({
    pickup_address: '',
    drop_address: '',
    temp_min: 2,
    temp_max: 8,
    expected_weight: ''
  });
  const navigate = useNavigate();

  const fetchWorkOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const [woRes, featuresRes] = await Promise.all([
        opsApi.getWorkOrders(params),
        opsApi.getTenantFeatures()
      ]);
      setWorkOrders(woRes.data);
      setFeatures(featuresRes.data);
    } catch (error) {
      toast.error('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchWorkOrders();
  }, [fetchWorkOrders]);

  const handleCreateWorkOrder = async () => {
    if (!newWO.pickup_address || !newWO.drop_address) {
      toast.error('Pickup and drop addresses are required');
      return;
    }
    setCreating(true);
    try {
      const response = await opsApi.createWorkOrder({
        ...newWO,
        expected_weight: newWO.expected_weight ? parseFloat(newWO.expected_weight) : null
      });
      toast.success('Work order created');
      setCreateDialogOpen(false);
      setNewWO({ pickup_address: '', drop_address: '', temp_min: 2, temp_max: 8, expected_weight: '' });
      fetchWorkOrders();
      // Navigate to the new work order
      navigate(`/ops/work-orders/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create work order');
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      assigned: 'bg-slate-500/20 text-slate-400 border-slate-500',
      started: 'bg-blue-500/20 text-blue-400 border-blue-500',
      in_transit: 'bg-amber-500/20 text-amber-400 border-amber-500',
      completed: 'bg-green-500/20 text-green-400 border-green-500',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500'
    };
    return <Badge variant="outline" className={styles[status] || styles.assigned}>{status}</Badge>;
  };

  const filteredWorkOrders = workOrders.filter(wo =>
    wo.work_order_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    wo.pickup_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    wo.drop_address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Work Orders</h1>
          <p className="text-slate-500 dark:text-slate-400">Manage delivery work orders</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={fetchWorkOrders}
            className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="create-wo-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Work Order
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-slate-900 dark:text-white">Create Work Order</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Pickup Address</Label>
                  <Input
                    value={newWO.pickup_address}
                    onChange={(e) => setNewWO({ ...newWO, pickup_address: e.target.value })}
                    placeholder="Warehouse, Street, City"
                    className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white mt-1"
                    data-testid="wo-pickup-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Drop Address</Label>
                  <Input
                    value={newWO.drop_address}
                    onChange={(e) => setNewWO({ ...newWO, drop_address: e.target.value })}
                    placeholder="Store, Street, City"
                    className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white mt-1"
                    data-testid="wo-drop-input"
                  />
                </div>
                {features.temp?.enabled && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700 dark:text-slate-300">Min Temp (°C)</Label>
                      <Input
                        type="number"
                        value={newWO.temp_min}
                        onChange={(e) => setNewWO({ ...newWO, temp_min: parseFloat(e.target.value) })}
                        className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 dark:text-slate-300">Max Temp (°C)</Label>
                      <Input
                        type="number"
                        value={newWO.temp_max}
                        onChange={(e) => setNewWO({ ...newWO, temp_max: parseFloat(e.target.value) })}
                        className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white mt-1"
                      />
                    </div>
                  </div>
                )}
                {features.weight?.enabled && (
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">Expected Weight (kg)</Label>
                    <Input
                      type="number"
                      value={newWO.expected_weight}
                      onChange={(e) => setNewWO({ ...newWO, expected_weight: e.target.value })}
                      placeholder="0.0"
                      className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white mt-1"
                    />
                  </div>
                )}
                <Button 
                  onClick={handleCreateWorkOrder}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  disabled={creating}
                  data-testid="submit-create-wo"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Work Order
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white" data-testid="wo-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
            <SelectItem value="all" className="text-slate-900 dark:text-white">All Statuses</SelectItem>
            <SelectItem value="assigned" className="text-slate-900 dark:text-white">Assigned</SelectItem>
            <SelectItem value="started" className="text-slate-900 dark:text-white">Started</SelectItem>
            <SelectItem value="in_transit" className="text-slate-900 dark:text-white">In Transit</SelectItem>
            <SelectItem value="completed" className="text-slate-900 dark:text-white">Completed</SelectItem>
            <SelectItem value="cancelled" className="text-slate-900 dark:text-white">Cancelled</SelectItem>
          </SelectContent>
        </Select>

        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by WO#, pickup, or drop address..."
            className="pl-10 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
            data-testid="wo-search"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {['assigned', 'started', 'in_transit', 'completed', 'cancelled'].map(status => {
          const count = workOrders.filter(wo => wo.status === status).length;
          return (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`bg-white dark:bg-slate-900 rounded-lg p-3 border transition-colors ${
                statusFilter === status ? 'border-teal-500' : 'border-slate-200 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-700'
              }`}
            >
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{count}</p>
              <p className="text-slate-500 dark:text-slate-400 text-sm capitalize">{status.replace('_', ' ')}</p>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-200 dark:border-slate-800">
              <TableHead className="text-slate-500 dark:text-slate-400">WO #</TableHead>
              <TableHead className="text-slate-500 dark:text-slate-400">Pickup</TableHead>
              <TableHead className="text-slate-500 dark:text-slate-400">Drop</TableHead>
              {features.temp?.enabled && (
                <TableHead className="text-slate-500 dark:text-slate-400">Temp Range</TableHead>
              )}
              <TableHead className="text-slate-500 dark:text-slate-400">Status</TableHead>
              <TableHead className="text-slate-500 dark:text-slate-400">Created</TableHead>
              <TableHead className="text-slate-500 dark:text-slate-400"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={features.temp?.enabled ? 7 : 6} className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto" />
                </TableCell>
              </TableRow>
            ) : filteredWorkOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={features.temp?.enabled ? 7 : 6} className="text-center py-12 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  No work orders found
                </TableCell>
              </TableRow>
            ) : (
              filteredWorkOrders.map((wo) => (
                <TableRow 
                  key={wo.id} 
                  className="border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                  onClick={() => navigate(`/ops/work-orders/${wo.id}`)}
                  data-testid={`wo-row-${wo.work_order_number}`}
                >
                  <TableCell className="text-slate-900 dark:text-white font-mono text-sm">{wo.work_order_number}</TableCell>
                  <TableCell className="text-slate-700 dark:text-slate-300 text-sm">
                    <div className="flex items-center gap-2 max-w-40">
                      <MapPin className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0" />
                      <span className="truncate">{wo.pickup_address || '-'}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-700 dark:text-slate-300 text-sm">
                    <div className="flex items-center gap-2 max-w-40">
                      <MapPin className="w-4 h-4 text-red-500 dark:text-red-400 flex-shrink-0" />
                      <span className="truncate">{wo.drop_address || '-'}</span>
                    </div>
                  </TableCell>
                  {features.temp?.enabled && (
                    <TableCell className="text-slate-700 dark:text-slate-300 text-sm">
                      {wo.temp_min !== null && wo.temp_max !== null ? (
                        <div className="flex items-center gap-1">
                          <Thermometer className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                          {wo.temp_min}°C - {wo.temp_max}°C
                        </div>
                      ) : '-'}
                    </TableCell>
                  )}
                  <TableCell>{getStatusBadge(wo.status)}</TableCell>
                  <TableCell className="text-slate-500 dark:text-slate-400 text-sm">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {new Date(wo.created_at).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <ChevronRight className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
